import React, { useState, useRef, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppContext } from "./context/AppContext";
import { useOldManVoice } from "./useOldManVoice";

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const MAX_GUESSES = 3;
const MAX_QUESTIONS = 5;
const LEADERS_PER_GAME = 5;

const ALL_LEADERS = [
  "Alexander the Great","Julius Caesar","Cleopatra","Napoleon Bonaparte",
  "George Washington","Abraham Lincoln","Winston Churchill",
  "Mahatma Gandhi","Nelson Mandela",
  "Queen Elizabeth I","Queen Elizabeth II","Catherine the Great",
  "Theodore Roosevelt","Franklin D. Roosevelt","John F. Kennedy",
  "Vladimir Lenin","Leon Trotsky","Genghis Khan","Attila the Hun",
  "Charlemagne","Julius Caesar","King Henry VIII","Oliver Cromwell",
  "Otto von Bismarck","Abraham Lincoln","Simón Bolívar","Sun Yat-sen",
  "Deng Xiaoping","Mikhail Gorbachev","Boris Yeltsin",
  "Angela Merkel","Margaret Thatcher","Tony Blair",
  "Charles de Gaulle","Benito Mussolini","Francisco Franco",
  "Jawaharlal Nehru","Indira Gandhi","Atatürk","Saladin",
  "Akbar the Great","Frederick the Great","Peter the Great","Louis XIV",
  "Queen Victoria","King George III","Abraham Lincoln","Martin Luther King Jr.",
  "Rosa Parks","Harriet Tubman","Frederick Douglass","Sojourner Truth",
  "Susan B. Anthony","Eleanor Roosevelt","Amelia Earhart",
  "Theodore Roosevelt","Woodrow Wilson","Harry S. Truman",
  "Dwight D. Eisenhower","Lyndon B. Johnson","Richard Nixon","Ronald Reagan",
  "George H. W. Bush","Bill Clinton","George W. Bush","Barack Obama",
  "Donald Trump","Joe Biden","Justin Trudeau","Emmanuel Macron",
  "Xi Jinping","Narendra Modi","Recep Tayyip Erdoğan","Jair Bolsonaro",
  "Lula da Silva","Jacinda Ardern","Scott Morrison","Shinzo Abe",
  "Moon Jae-in","Park Geun-hye","Yoon Suk-yeol"
];

// Deduplicate the list
const UNIQUE_LEADERS = [...new Set(ALL_LEADERS)];

function pickRandom(list, count) {
  const shuffled = [...list];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

export default function LeadersGameScreen() {
  const navigate = useNavigate();
  const { player, isGuest, guestPlays, setGuestPlays, wins, setWins, losses, setLosses } = useContext(AppContext);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [guessesLeft, setGuessesLeft] = useState(0);
  const [questionsLeft, setQuestionsLeft] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedLeader, setSelectedLeader] = useState("");
  const [randomLeader, setRandomLeader] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [gameLeaders, setGameLeaders] = useState([]);

  const [showMetrics, setShowMetrics] = useState(false);
  const [lastMetrics, setLastMetrics] = useState(null);

  const messagesEndRef = useRef(null);
  const gameStartTimeRef = useRef(null);
  const { speak } = useOldManVoice();

  const API_ENDPOINT = process.env.REACT_APP_API_URL;

  const GUEST_LIMIT = 3;

  const startNewGame = () => {
    if (player.trim() === "") return;
    if (isGuest && guestPlays.leaders >= GUEST_LIMIT) {
      setShowGuestPrompt(true);
      return;
    }
    if (isGuest) {
      setGuestPlays((p) => ({ ...p, leaders: p.leaders + 1 }));
    }
    const picked = pickRandom(UNIQUE_LEADERS, LEADERS_PER_GAME);
    const mystery = picked[Math.floor(Math.random() * picked.length)];
    setGameLeaders(["", ...picked]);
    setMessages([
      {
        sender: "assistant",
        text: `I am one of these ${LEADERS_PER_GAME} famous leaders: ${picked.join(", ")}. Ask me questions to find out which one!`
      }
    ]);
    setGuessesLeft(MAX_GUESSES);
    setQuestionsLeft(MAX_QUESTIONS);
    setSelectedLeader("");
    setRandomLeader(mystery);
    setGameOver(false);
    gameStartTimeRef.current = Date.now();
  };

  const submitHighScore = async (score) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/chat`,
        { ID: player, amount: score, gameType: "leaders" },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = typeof res.data.body === "string" ? JSON.parse(res.data.body) : res.data.body ?? res.data;
      return data?.Score ?? score;
    } catch {
      return score;
    }
  };

  const makeGuess = async () => {
    if (gameOver || guessesLeft < 1) return;

    const isCorrect = selectedLeader === randomLeader;
    const newGuessesLeft = guessesLeft - 1;
    setGuessesLeft(newGuessesLeft);

    if (isCorrect) {
      const guessesUsed = MAX_GUESSES - newGuessesLeft;
      const questionsUsed = MAX_QUESTIONS - questionsLeft;
      const score = (newGuessesLeft + questionsLeft) * 10;
      const elapsed = gameStartTimeRef.current ? Date.now() - gameStartTimeRef.current : 0;

      const totalScore = isGuest ? score : await submitHighScore(score);

      setMessages((p) => [
        ...p,
        { sender: "assistant", text: `🎉 Correct! I am ${randomLeader}!` },
        {
          sender: "assistant",
          text: `It took you ${guessesUsed} guess(es) and ${questionsUsed} question(s). Score: ${score} 🏆 | Total: ${totalScore}`
        }
      ]);

      setWins((w) => {
        const newWins = w + 1;
        setLastMetrics({
          won: true,
          elapsed,
          guessesUsed,
          questionsUsed,
          score,
          totalScore,
          wins: newWins,
          losses,
          maxGuesses: MAX_GUESSES,
          maxQuestions: MAX_QUESTIONS,
        });
        return newWins;
      });
      setShowMetrics(true);
      setGameOver(true);
      return;
    }

    if (newGuessesLeft === 0) {
      const elapsed = gameStartTimeRef.current ? Date.now() - gameStartTimeRef.current : 0;
      setMessages((p) => [
        ...p,
        { sender: "assistant", text: `No more guesses! I was ${randomLeader}.` }
      ]);
      setLosses((l) => {
        const newLosses = l + 1;
        setLastMetrics({
          won: false,
          answer: randomLeader,
          elapsed,
          guessesUsed: MAX_GUESSES,
          questionsUsed: MAX_QUESTIONS - questionsLeft,
          score: 0,
          totalScore: null,
          wins,
          losses: newLosses,
          maxGuesses: MAX_GUESSES,
          maxQuestions: MAX_QUESTIONS,
        });
        return newLosses;
      });
      setShowMetrics(true);
      setGameOver(true);
      return;
    }

    await sendMessage(`The user guessed ${selectedLeader}`);
  };

  const askQuestion = async () => {
    if (gameOver || questionsLeft < 1 || !input.trim()) return;
    setQuestionsLeft((p) => p - 1);
    sendMessage(input);
  };

  const sendMessage = async (text) => {
    const updated = [...messages, { sender: "user", text }];
    setMessages(updated);
    setInput("");

    try {
      const res = await axios.post(
        API_ENDPOINT,
        {
          personas: [
            `You are the famous world leader ${randomLeader}, speaking in first person.`,
            "Respond using facts about your life, era, country, policies, achievements, and legacy.",
            "Do not reveal your name directly.",
            "If the user guesses wrong, tell them they are wrong humorously without revealing your identity.",
            "If the user tries to cheat or asks your name directly, respond with a playful joke that does not answer.",
            "Only respond with at most 20 words.",
            "You will avoid political persuasion or harmful content."
          ],
          messages: updated.map((m) => ({
            role: m.sender === "assistant" ? "assistant" : "user",
            content: m.text
          }))
        },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      const data =
        typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body ?? res.data;

      setMessages((p) => [
        ...p,
        { sender: "assistant", text: data?.response || "No response." }
      ]);
    } catch {
      setMessages((p) => [
        ...p,
        { sender: "assistant", text: "Something went wrong." }
      ]);
    }
  };

  return (
    <div style={styles.page}>
      {showMetrics && lastMetrics && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.metricsIcon}>{lastMetrics.won ? "🏆" : "😔"}</div>
            <h2 style={styles.modalTitle}>{lastMetrics.won ? "You Won!" : "Game Over"}</h2>
            {!lastMetrics.won && lastMetrics.answer && (
              <div style={styles.answerReveal}>
                <div style={styles.answerLabel}>The answer was</div>
                <div style={styles.answerName}>{lastMetrics.answer}</div>
              </div>
            )}
            <div style={styles.metricsGrid}>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Time</div>
                <div style={styles.metricValue}>{formatTime(lastMetrics.elapsed)}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Guesses Used</div>
                <div style={styles.metricValue}>{lastMetrics.guessesUsed} / {lastMetrics.maxGuesses}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Questions Used</div>
                <div style={styles.metricValue}>{lastMetrics.questionsUsed} / {lastMetrics.maxQuestions}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Round Score</div>
                <div style={styles.metricValue}>{lastMetrics.score}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Wins</div>
                <div style={{ ...styles.metricValue, color: "#28a745" }}>{lastMetrics.wins}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Losses</div>
                <div style={{ ...styles.metricValue, color: "#dc3545" }}>{lastMetrics.losses}</div>
              </div>
            </div>
            <button style={styles.primaryBtn} onClick={() => { setShowMetrics(false); startNewGame(); }}>
              Play Again
            </button>
            <button style={{ ...styles.secondaryOutlineBtn, marginTop: 8 }} onClick={() => { setShowMetrics(false); navigate("/"); }}>
              Home
            </button>
            <button style={styles.dismissBtn} onClick={() => setShowMetrics(false)}>
              Close
            </button>
          </div>
        </div>
      )}
      {showGuestPrompt && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>You've used your 3 guest plays!</h2>
            <p style={styles.modalDesc}>Create an account or log in to keep playing and save your scores.</p>
            <button style={styles.primaryBtn} onClick={() => navigate("/newuser")}>
              Create Account
            </button>
            <button style={styles.secondaryOutlineBtn} onClick={() => navigate("/matrix")}>
              Log In
            </button>
            <button style={styles.dismissBtn} onClick={() => setShowGuestPrompt(false)}>
              Maybe later
            </button>
          </div>
        </div>
      )}
      <div style={styles.card}>
        <header style={styles.header}>
          <strong>Guess the Famous Leader | User: {player}</strong>
          <div style={styles.stats}>
            🎯 {guessesLeft} guesses · 💬 {questionsLeft} questions
          </div>
          <div style={styles.headerButtons}>
            <button style={styles.smallBtn} onClick={() => navigate("/")}>
              Home
            </button>
            <button style={styles.smallBtn} onClick={startNewGame}>
              New Game
            </button>
          </div>
        </header>

        <main style={styles.messages}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                ...styles.bubble,
                ...(m.sender === "user" ? styles.user : styles.bot),
                ...(m.sender === "assistant" ? { display: "flex", alignItems: "flex-end", gap: 6 } : {})
              }}
            >
              <span>{m.text}</span>
              {m.sender === "assistant" && (
                <button
                  onClick={() => speak(m.text, randomLeader)}
                  style={styles.speakBtn}
                  title="Read aloud"
                >
                  🔊
                </button>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </main>

        <footer style={styles.footer}>
          <div style={styles.row}>
            <input
              style={styles.input}
              disabled={gameOver}
              placeholder="Ask a question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button
              style={styles.primaryBtn}
              disabled={gameOver}
              onClick={askQuestion}
            >
              Send
            </button>
          </div>
          <div style={styles.row}>
            <select
              style={styles.input}
              value={selectedLeader}
              disabled={gameOver}
              onChange={(e) => setSelectedLeader(e.target.value)}
            >
              {gameLeaders.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
            <button
              style={styles.primaryBtn}
              disabled={gameOver}
              onClick={makeGuess}
            >
              Guess
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "url(/world_leaders_background_img.jpg) center / cover no-repeat",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: 12
  },
  card: {
    width: "100%",
    maxWidth: 500,
    background: "#ffffffda",
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxHeight: "calc(100dvh - 24px)",
    minHeight: 0
  },
  header: {
    padding: 12,
    borderBottom: "1px solid #eee",
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  stats: {
    fontSize: 14,
    opacity: 0.8
  },
  headerButtons: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },
  messages: {
    flex: 1,
    padding: 12,
    overflowY: "auto",
    backgroundColor: "transparent"
  },
  bubble: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: 18,
    marginBottom: 10,
    fontSize: 15
  },
  user: {
    alignSelf: "flex-end",
    background: "#dcf8c6"
  },
  bot: {
    alignSelf: "flex-start",
    background: "#eee"
  },
  speakBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    padding: "0 2px",
    lineHeight: 1,
    flexShrink: 0,
    opacity: 0.7
  },
  footer: {
    padding: 10,
    borderTop: "1px solid #eee",
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  row: {
    display: "flex",
    gap: 8
  },
  input: {
    width: "100%",
    padding: 12,
    borderRadius: 20,
    border: "1px solid #8f3f3fff",
    fontSize: 16
  },
  primaryBtn: {
    flex: 1,
    padding: "6px 12px",
    borderRadius: 20,
    border: "none",
    background: "#9b59b6",
    color: "#fff",
    fontSize: 16
  },
  smallBtn: {
    flex: 1,
    padding: "6px 12px",
    borderRadius: 14,
    border: "none",
    background: "#9b59b6",
    color: "#fff",
    fontSize: 14
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modal: {
    background: "#fff",
    borderRadius: 18,
    padding: "28px 24px",
    maxWidth: 340,
    width: "90%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    boxShadow: "0 8px 32px rgba(0,0,0,0.22)"
  },
  modalTitle: {
    margin: "0 0 6px",
    fontSize: 19,
    fontWeight: 700,
    textAlign: "center"
  },
  modalDesc: {
    margin: "0 0 14px",
    fontSize: 14,
    color: "#555",
    textAlign: "center",
    lineHeight: 1.5
  },
  secondaryOutlineBtn: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 20,
    border: "1px solid #9b59b6",
    background: "#fff",
    color: "#9b59b6",
    fontSize: 15,
    cursor: "pointer",
    marginTop: 8
  },
  dismissBtn: {
    background: "none",
    border: "none",
    color: "#888",
    cursor: "pointer",
    fontSize: 13,
    marginTop: 6,
    textDecoration: "underline"
  },
  answerReveal: {
    background: "#fff3f3",
    border: "1.5px solid #dc3545",
    borderRadius: 10,
    padding: "10px 14px",
    textAlign: "center",
    margin: "4px 0 2px"
  },
  answerLabel: {
    fontSize: 11,
    color: "#dc3545",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4
  },
  answerName: {
    fontSize: 18,
    fontWeight: 700,
    color: "#222"
  },
  metricsIcon: {
    fontSize: 40,
    textAlign: "center",
    marginBottom: 4
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    margin: "14px 0"
  },
  metricCard: {
    background: "#f5f7fa",
    borderRadius: 12,
    padding: "10px 8px",
    textAlign: "center"
  },
  metricLabel: {
    fontSize: 11,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4
  },
  metricValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#222"
  }
};
